import React, { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'hooks';
import { Alert } from 'reactstrap';

import { AuthTags } from '~constants/authTags';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatTimeString } from '~libs/dayjs';
import {
  buildTimeBandRows,
  findLocalIssues,
  isLockedOverride,
  mapErrorsToRows,
  TimeBandRow,
  toSavePayload,
} from '~support-at-home/lib/timeBands';

import { Button, ErrorResponse, Toolbar } from '~components';
import Icon, { faExclamationTriangle } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import { CompanyShift, ShiftWithTimes } from '~lookups/models';

import columnsFactory from '~support-at-home/forms/time-bands/list';

import { useGetShiftsQuery } from '~lookups/actions/shifts';
import {
  CompanyShiftsErrorResponse,
  useGetCompanyShiftsQuery,
  useSaveCompanyShiftsMutation,
} from '~support-at-home/actions/companyShifts';

import { selectUserTags } from '~main/selectors/login';

interface ComponentProps {
  isActive: boolean;
}

/**
 * RTK Query reports a skipped query as having no data at all, and this tab's queries are skipped for
 * as long as another tab is in front of it. Defaulting that to a `[]` literal in the destructure
 * would mint a new array identity on every render, which invalidates every memo built from it on
 * every render - and an effect keyed on one of those memos then never settles. One fixed value per
 * collection keeps the identity stable; frozen so shared state cannot be written through.
 */
const NO_SHIFTS: readonly ShiftWithTimes[] = Object.freeze([]);
const NO_COMPANY_SHIFTS: readonly CompanyShift[] = Object.freeze([]);

/**
 * Decision D: the change is retroactive for unclaimed work, so the confirmation says the same thing
 * the warning above the table says rather than reading as forward-only.
 */
const SAVED_MESSAGE =
  'Time bands saved - every appointment not yet claimed, including work already delivered, will be priced on these ' +
  'times.';

/**
 * A whole-day band holding times of its own cannot be written from this tab: the save takes the
 * three editable bands and nothing else, so neither saving nor resetting here can clear one. Naming
 * the band and its times, and saying plainly that the tab cannot clear them, is the whole of what
 * this tab can honestly do about it.
 */
const lockedOverrideTimes = (rows: TimeBandRow[]): string =>
  rows
    .map(({ name, start_time, end_time }) => {
      return `${name} is priced on ${formatTimeString(start_time)} to ${formatTimeString(end_time)}.`;
    })
    .join(' ');

/** A band whose override was removed now tracks the platform default, which is worth saying. */
const clearedMessage = (cleared?: number): string => {
  if (!cleared || cleared < 1) return '';
  const bands = cleared === 1 ? '1 band is' : `${cleared} bands are`;
  return ` ${bands} back on the platform default and will follow any later change to it.`;
};

const TimeBands: React.FC<ComponentProps> = ({ isActive }) => {
  const userTags = useAppSelector(selectUserTags);
  // Decision C: the tab is gated on the budget permissions the API already enforces, so nothing has
  // to be granted before a company can use it.
  const canView = userTags.includes(AuthTags.CLIENTS.BUDGETS.VIEW);
  const canUpdate = userTags.includes(AuthTags.CLIENTS.BUDGETS.UPDATE);
  const shouldLoad = isActive && canView;

  const {
    data: shifts = NO_SHIFTS,
    isLoading: isLoadingShifts,
    error: shiftsError,
  } = useGetShiftsQuery(undefined, { skip: !shouldLoad });
  const {
    data: companyShifts = NO_COMPANY_SHIFTS,
    isLoading: isLoadingCompanyShifts,
    error: companyShiftsError,
  } = useGetCompanyShiftsQuery(undefined, { skip: !shouldLoad });
  const [saveCompanyShifts, { isLoading: isSaving }] = useSaveCompanyShiftsMutation();

  // The effective set: the company's times where it has them, the platform's where it has not.
  const effectiveRows = useMemo(() => buildTimeBandRows(shifts, companyShifts), [shifts, companyShifts]);

  const [rows, setRows] = useState<TimeBandRow[]>(effectiveRows);
  const [serverIssues, setServerIssues] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // Reload the editable set whenever the persisted one changes, including the refetch a save
  // triggers. The save confirmation deliberately survives that refetch - it is the confirmation of
  // the change the refetch is delivering - and is cleared by the next edit instead.
  useEffect(() => {
    setRows(effectiveRows);
    setServerIssues({});
    setSaveError(null);
  }, [effectiveRows]);

  const lockedOverrides = useMemo(() => rows.filter(isLockedOverride), [rows]);

  const localIssues = useMemo(() => findLocalIssues(rows), [rows]);
  const issues = useMemo(() => ({ ...serverIssues, ...localIssues }), [serverIssues, localIssues]);

  const tableRows = useMemo(() => rows.map((row) => ({ ...row, issue: issues[row.id] ?? '' })), [rows, issues]);

  const isDirty = useMemo(
    () =>
      rows.some((row) => {
        const original = effectiveRows.find(({ id }) => id === row.id);
        return !original || original.start_time !== row.start_time || original.end_time !== row.end_time;
      }),
    [rows, effectiveRows],
  );

  // Any edit invalidates the reasons the API gave for the previous set, which it judged as a whole.
  const clearSaveFeedback = () => {
    setServerIssues({});
    setSaveError(null);
    setSavedMessage(null);
  };

  const handleChange = (next: Row[]) => {
    setRows(next.map((row) => ({ ...(row as TimeBandRow), issue: '' })));
    clearSaveFeedback();
  };

  const handleReset = (target: TimeBandRow) => {
    setRows((previous) =>
      previous.map((row) =>
        row.id === target.id
          ? { ...row, start_time: row.default_start_time, end_time: row.default_end_time, issue: '' }
          : row,
      ),
    );
    clearSaveFeedback();
  };

  const handleSave = async () => {
    clearSaveFeedback();
    try {
      const result = await saveCompanyShifts({ shifts: toSavePayload(rows) }).unwrap();
      setSavedMessage(`${SAVED_MESSAGE}${clearedMessage(result?.cleared)}`);
    } catch (error) {
      const rejection = error as { message?: string; data?: CompanyShiftsErrorResponse };
      const { issues: rowIssues, unmatched } = mapErrorsToRows(rows, rejection?.data?.errors);
      setServerIssues(rowIssues);
      setSaveError(
        [rejection?.data?.message ?? rejection?.message ?? 'Failed to save time bands', ...unmatched].join(' '),
      );
    }
  };

  if (!canView) {
    return (
      <div className="p-2">
        <Alert color="info" data-cy="sah-time-bands-no-access">
          You do not have permission to view Support at Home time bands.
        </Alert>
      </div>
    );
  }

  const isLoading = isLoadingShifts || isLoadingCompanyShifts;
  const loadErrorMessage = [shiftsError, companyShiftsError].filter(Boolean).map(getErrorMessage).join(' ');
  const hasLocalIssues = Object.keys(localIssues).length > 0;

  // `isSaving` is named here as well as in the button's `loading`, on purpose. `Button` computes
  // `disabled={loading || props.disabled}` and then spreads `props` over that, so a `disabled` prop of
  // its own wins and `loading` on its own leaves the button clickable. Naming the in-flight save here
  // is therefore what stops a second click sending a second PUT - nothing else does, since an RTK
  // Query mutation happily runs overlapping calls.
  const cannotSave = !isDirty || hasLocalIssues || isLoading || isSaving;

  const columns = columnsFactory(handleReset, canUpdate, Object.keys(issues).length > 0);

  return (
    <div className="p-2">
      <ErrorResponse message={loadErrorMessage} dataCy="sah-time-bands-load-error" />

      <Toolbar
        id="sah-time-bands"
        rightCluster={
          <Button
            id="sah-time-bands-save-button"
            dataCy="sah-time-bands-save-button"
            authTag={AuthTags.CLIENTS.BUDGETS.UPDATE}
            size="sm"
            color="success"
            disabled={cannotSave}
            loading={isSaving}
            onClick={handleSave}
          >
            Save Time Bands
          </Button>
        }
      />

      {/* Save status belongs with the action that produced it, so it sits under the toolbar holding
          the save button rather than beneath the table. */}
      {savedMessage && (
        <Alert color="success" className="mt-2" data-cy="sah-time-bands-saved">
          {savedMessage}
        </Alert>
      )}
      <ErrorResponse message={saveError} dataCy="sah-time-bands-save-error" />

      {lockedOverrides.length > 0 && (
        <Alert color="danger" className="d-flex align-items-start gap-2 mt-2" data-cy="sah-time-bands-locked-override">
          <Icon icon={faExclamationTriangle} className="mt-1" />
          <div>
            <strong>A band that applies for its whole day is priced on times of its own.</strong>{' '}
            {lockedOverrideTimes(lockedOverrides)} Claims use those times, not the whole day the platform sets for the
            band. This tab cannot change or clear them, because it only ever saves the three weekday bands. Contact
            support to have the stored times removed if they were not intended.
          </div>
        </Alert>
      )}

      <Alert color="warning" className="d-flex align-items-start gap-2 mt-2">
        <Icon icon={faExclamationTriangle} className="mt-1" />
        <div>
          <strong>Saving re-prices work that has not been claimed yet.</strong> A time band is applied when a claim is
          built, not when an appointment is booked. Changing these times therefore changes the price of every
          appointment that is already booked but not yet claimed, including work that has already been delivered.
        </div>
      </Alert>

      <p className="text-muted small mb-2" data-cy="sah-time-bands-rules">
        The three weekday bands must together cover every minute of a weekday, with no gap and no overlap. A band that
        ends at 12:00 AM ends at the end of the day. The Saturday, Sunday and public holiday bands apply for their whole
        day and cannot be changed.
      </p>

      <TableView
        id="sah-time-bands"
        columns={columns}
        data={tableRows}
        isLoading={isLoading}
        readonly={!canUpdate}
        editonly={canUpdate}
        canDeleteRow={false}
        showAddRow={false}
        striped
        onChange={handleChange}
        emptyMessage="No time bands found"
      />
    </div>
  );
};

export default TimeBands;
